import type { QueryClient } from "@tanstack/react-query";
import { createFileRoute, notFound, redirect } from "@tanstack/react-router";
import z from "zod";

import { getDatasourceFileUrl } from "@/core/lib/api";
import { fetchDatasourcesByIds } from "@/core/lib/datasources-by-ids";
import { coerceToStringArray } from "@/core/lib/search-params";
import type { ConversationDatasource, Datasource } from "@/openapi";
import {
  getBatchSummaryOptions,
  getDocumentAnalysisTemplateOptions,
  getDocumentAnalysisTemplatesOptions,
  listProjectVectorizedDatasourcesOptions,
} from "@/openapi/@tanstack/react-query.gen";

export interface BulkResultDocument {
  datasourceId: string;
  name: string;
  pdfUrl: string;
  // set for persisted batches (reopened via batchId), used to load per-run results
  runNumber?: number;
}

const searchSchema = z.object({
  // live run: templateId + datasourceIds; persisted revisit: batchId
  templateId: z.uuid().optional(),
  datasourceIds: z
    .preprocess(coerceToStringArray, z.array(z.uuid()))
    .optional(),
  selectedConditions: z
    .preprocess(coerceToStringArray, z.array(z.string()))
    .optional(),
  projectId: z.uuid().optional(),
  batchId: z.uuid().optional(),
  promotionRequestId: z.uuid().optional(),
});

function toDocument(
  datasource: Datasource | ConversationDatasource,
): BulkResultDocument {
  return {
    datasourceId: datasource.id,
    name: datasource.name,
    // documents without a viewable blob still appear in the table; the viewer
    // shows a "no preview" placeholder when selected
    pdfUrl: getDatasourceFileUrl(datasource) ?? "",
  };
}

export const Route = createFileRoute(
  "/_protected/document-analysis/bulk-results",
)({
  validateSearch: searchSchema,
  loaderDeps: ({
    search: {
      templateId,
      datasourceIds,
      projectId,
      batchId,
      promotionRequestId,
    },
  }) => ({
    templateId,
    datasourceIds,
    projectId,
    batchId,
    promotionRequestId,
  }),
  loader: loadBulkResultsRoute,
});

export type BulkResultsLoaderDeps = Pick<
  z.infer<typeof searchSchema>,
  | "templateId"
  | "datasourceIds"
  | "projectId"
  | "batchId"
  | "promotionRequestId"
>;

export async function loadBulkResultsRoute({
  context: { queryClient, ...context },
  deps,
}: {
  context: {
    queryClient: QueryClient;
    user: { organization?: { id: string } | null };
  };
  deps: BulkResultsLoaderDeps;
}) {
  const orgId = context.user.organization?.id;
  if (!orgId) {
    throw redirect({ to: "/no-organization", throw: true });
  }

  const [summary, templatesData] = await Promise.all([
    deps.batchId
      ? queryClient.query(
          getBatchSummaryOptions({
            path: { org_id: orgId, batch_id: deps.batchId },
          }),
        )
      : undefined,
    deps.promotionRequestId && deps.templateId
      ? queryClient.query(
          getDocumentAnalysisTemplateOptions({
            path: { org_id: orgId, template_id: deps.templateId },
            query: { promotion_request_id: deps.promotionRequestId },
          }),
        )
      : queryClient.query(
          getDocumentAnalysisTemplatesOptions({ path: { org_id: orgId } }),
        ),
  ]);

  const datasourcesData: (Datasource | ConversationDatasource)[] =
    deps.projectId
      ? (
          await queryClient.query(
            listProjectVectorizedDatasourcesOptions({
              path: { project_id: deps.projectId, org_id: orgId },
            }),
          )
        ).map((file) => file.datasource)
      : await fetchDatasourcesByIds(
          queryClient,
          orgId,
          summary
            ? summary.documents.map((d) => d.datasource_id)
            : (deps.datasourceIds ?? []),
        );

  const findDocument = (
    datasourceId: string,
    runNumber?: number,
  ): BulkResultDocument | null => {
    const datasource = datasourcesData.find((d) => d.id === datasourceId);
    if (!datasource) return null;
    const doc = toDocument(datasource);
    return runNumber === undefined ? doc : { ...doc, runNumber };
  };

  // persisted revisit: resolve the batch to its documents + shared template
  if (summary) {
    const template = Array.isArray(templatesData)
      ? templatesData.find((item) => item.id === summary.template_id)
      : templatesData;
    if (!template) {
      throw notFound();
    }
    const documents = summary.documents
      .map((d) => findDocument(d.datasource_id, d.run_number))
      .filter((d): d is BulkResultDocument => d !== null);
    return { template, documents, projectId: summary.project_id };
  }

  // live run: stream against the selected datasources
  const template = Array.isArray(templatesData)
    ? templatesData.find((item) => item.id === deps.templateId)
    : templatesData;
  if (!template) {
    throw notFound();
  }
  const resolved = (deps.datasourceIds ?? []).map((id) => findDocument(id));
  // a stale/shared URL may reference a datasource that was deleted or belongs
  // to another org; fail fast rather than rendering a partial batch
  if (resolved.includes(null)) {
    throw notFound();
  }
  const documents = resolved.filter((d): d is BulkResultDocument => d !== null);
  return { template, documents, projectId: deps.projectId ?? null };
}
